import React, { FC, useState, useEffect } from "react";
import {
    Typography,
    Grid,
    Tabs,
    Tab,
    Box,
    Container,
    Link,
    ToggleButtonGroup,
    ToggleButton,
    Chip,
} from "@mui/material";
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import { useParams } from "react-router-dom";
import ListIcon from "@mui/icons-material/List";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import Breadcrumb from "../../../../core/components/Breadcrumb";
import Spinner from "../../../layout/Spinner";
import { useBranchedStory } from "../hooks/useBranchedStory";
import { BranchedStoryType, getById, publishStory, reviewStory } from "../../../../client/api/branchedStory";
import { date } from "../../../../utils/date";
import { TabPanel } from "../../../programme/screens/ViewProgramme";
import { useAppNavigate } from "../../../../hooks/useAppNavigate";
import { BranchedStoryAccordionView } from "../components/BranchedStoryAccordionView";
import { BranchedStoryFlowChartDiagram } from "../components/BranchedStoryFlowChartDiagram";
import { StoryViewHeader } from "../../stories/components/StoryViewHeader";

const useStyles = makeStyles(() =>
    createStyles({
        header: {
            backgroundColor: "#F9F9FB",
        },
        textBreak: {
            whiteSpace: "pre-line",
        },
    }),
);
export const BranchedStoryDetailsScreen: FC = () => {
    const classes = useStyles();
    const params = useParams();
    const id = params.id!;
    const storyId = parseInt(id || "0", 10);
    const [tabValue, setTabValue] = useState<number>(0);
    const [actionLoading, setActionLoading] = useState<boolean>(false);
    const [storyData, setStoryData] = useState<BranchedStoryType | null>(null);
    const [loadingStory, setLoadingStory] = useState(false);
    const [error, setError] = useState<boolean>(false);
    const [structureView, setStructureView] = useState<"list" | "flowchart">("list");
    const { structure, loading } = useBranchedStory(storyId);
    const { push } = useAppNavigate();
    useEffect(() => {
        if (storyId) {
            setLoadingStory(true);
            getById(storyId)
                .then((story) => {
                    setStoryData(story);
                    setError(false);
                })
                .catch(() => {
                    setError(true);
                })
                .finally(() => {
                    setLoadingStory(false);
                });
        }
    }, [storyId]);
    const handleEdit = () => {
        // Navigate to edit mode - this will trigger the CRUD edit flow
        push(`/curation/branched-stories/edit/${storyData?.id}`);
    };
    const handleApprove = async () => {
        if (!storyData?.id) return;
        setActionLoading(true);
        try {
            await reviewStory(storyData.id);
            // Refresh story data to update isReviewed status
            const updatedData = await getById(storyData.id);
            setStoryData(updatedData);
        } catch (err) {
            console.error("Failed to approve story:", err);
        } finally {
            setActionLoading(false);
        }
    };
    const handlePublish = async () => {
        if (!storyData?.id) return;
        setActionLoading(true);
        try {
            await publishStory(storyData.id);
            // Refresh story data to update isPublished status
            const updatedData = await getById(storyData.id);
            setStoryData(updatedData);
        } catch (err) {
            console.error("Failed to publish story:", err);
        } finally {
            setActionLoading(false);
        }
    };
    if (error || ((loading || loadingStory) === false && !storyData)) {
        return <Typography color="error">Unable to load Branched Story</Typography>;
    }
    return (
        <>
            <Box p={3} className={classes.header}>
                <Box mb={2}>
                    <Breadcrumb
                        crumbs={[{ name: "Branched Stories", url: "/curation/branched-stories" }]}
                        current={storyData ? `${storyData.storyName}` : `Branched Story`}
                    />
                </Box>
            </Box>
            {!loadingStory && storyData ? (
                <>
                    <Box p={3}>
                        <Container maxWidth={"md"}>
                            <Tabs
                                value={tabValue}
                                onChange={(_event: React.ChangeEvent<unknown>, newValue: number) =>
                                    setTabValue(newValue)
                                }
                            >
                                <Tab label="Details" id="details-tab" />
                                <Tab label="Structure" id="structure-tab" />
                                <Tab label="Meta" id="meta-tab" />
                            </Tabs>
                            <TabPanel index={0} value={tabValue}>
                                <Grid container spacing={3}>
                                    <StoryViewHeader
                                        storyName={storyData.storyName}
                                        onEdit={handleEdit}
                                        onApprove={handleApprove}
                                        onPublish={handlePublish}
                                        isReviewed={storyData.isReviewed}
                                        isPublished={storyData.isPublished}
                                        loading={actionLoading}
                                    />
                                    <Grid item xs={12}>
                                        <Box mb={1}>
                                            <Typography variant={"h4"}>Author</Typography>
                                        </Box>
                                        <Typography>{storyData.author}</Typography>
                                    </Grid>
                                    {storyData.ageRange && (
                                        <Grid item xs={12} sm={6}>
                                            <Box mb={1}>
                                                <Typography variant={"h4"}>Age Range</Typography>
                                            </Box>
                                            <Typography>{storyData.ageRange}</Typography>
                                        </Grid>
                                    )}
                                    <Grid item xs={12} sm={6}>
                                        <Box mb={1}>
                                            <Typography variant={"h4"}>Story Type</Typography>
                                        </Box>
                                        <Typography>Branched Story</Typography>
                                    </Grid>
                                    {storyData.Themes && storyData.Themes.length > 0 && (
                                        <Grid item xs={12}>
                                            <Box mb={1}>
                                                <Typography variant={"h4"}>Themes</Typography>
                                            </Box>
                                            <Box display="flex" flexWrap="wrap" gap={1}>
                                                {storyData.Themes.map((theme) => (
                                                    <Chip
                                                        key={theme.id}
                                                        label={theme.name}
                                                        color="primary"
                                                        variant="outlined"
                                                    />
                                                ))}
                                            </Box>
                                        </Grid>
                                    )}
                                    {storyData.synopsis && (
                                        <Grid item xs={12}>
                                            <Box mb={1}>
                                                <Typography variant={"h4"}>Story Synopsis</Typography>
                                            </Box>
                                            <Typography className={classes.textBreak}>{storyData.synopsis}</Typography>
                                        </Grid>
                                    )}
                                    {storyData.resources && (
                                        <Grid item xs={12}>
                                            <Box mb={1}>
                                                <Typography variant={"h4"}>Additional Resources link - NI</Typography>
                                            </Box>
                                            <Typography
                                                className={classes.textBreak}
                                                component={Link}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                href={storyData.resources}
                                            >
                                                {storyData.resources}
                                            </Typography>
                                        </Grid>
                                    )}
                                    {storyData.resourcesROI && (
                                        <Grid item xs={12}>
                                            <Box mb={1}>
                                                <Typography variant={"h4"}>Additional Resources link - ROI</Typography>
                                            </Box>
                                            <Typography
                                                className={classes.textBreak}
                                                component={Link}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                href={storyData.resourcesROI}
                                            >
                                                {storyData.resourcesROI}
                                            </Typography>
                                        </Grid>
                                    )}
                                </Grid>
                            </TabPanel>
                            <TabPanel index={1} value={tabValue}>
                                {structure?.allParts?.length && structure.allParts.length > 0 ? (
                                    <>
                                        {/* View Toggle */}
                                        <Box mb={3} display="flex" justifyContent="center">
                                            <ToggleButtonGroup
                                                value={structureView}
                                                exclusive
                                                onChange={(_event, newView) => {
                                                    if (newView !== null) {
                                                        setStructureView(newView);
                                                    }
                                                }}
                                                aria-label="structure view"
                                            >
                                                <ToggleButton value="list" aria-label="list view">
                                                    <ListIcon style={{ marginRight: "8px" }} />
                                                    List View
                                                </ToggleButton>
                                                <ToggleButton value="flowchart" aria-label="flowchart view">
                                                    <AccountTreeIcon style={{ marginRight: "8px" }} />
                                                    Flowchart View
                                                </ToggleButton>
                                            </ToggleButtonGroup>
                                        </Box>

                                        {/* Conditional Rendering Based on View */}
                                        {structureView === "list" ? (
                                            <BranchedStoryAccordionView parts={structure.allParts} />
                                        ) : (
                                            <BranchedStoryFlowChartDiagram
                                                parts={structure.allParts}
                                                onPartClick={(partNumber) => {
                                                    // Switch to list view and scroll to part
                                                    setStructureView("list");
                                                    setTimeout(() => {
                                                        const element = document.getElementById(
                                                            `story-part-${partNumber}`,
                                                        );
                                                        if (element) {
                                                            element.scrollIntoView({
                                                                behavior: "smooth",
                                                                block: "start",
                                                            });
                                                            // Trigger click to expand accordion
                                                            const accordionButton = element.querySelector(
                                                                "[aria-expanded]",
                                                            ) as HTMLElement;
                                                            if (accordionButton) {
                                                                accordionButton.click();
                                                            }
                                                        }
                                                    }, 100);
                                                }}
                                            />
                                        )}
                                    </>
                                ) : (
                                    <Typography>No story structure available</Typography>
                                )}
                            </TabPanel>
                            <TabPanel index={2} value={tabValue}>
                                <Grid container spacing={3}>
                                    {storyData?.CreateUser?.firstName ? (
                                        <Grid item xs={12} md={6}>
                                            <Typography variant="h6">Created By</Typography>
                                            <Typography>{`${storyData?.CreateUser?.firstName} ${
                                                storyData?.CreateUser?.lastName ?? ""
                                            } ${
                                                storyData?.CreateUser?.email ? `/ ${storyData.CreateUser.email}` : ""
                                            }`}</Typography>
                                        </Grid>
                                    ) : null}
                                    {storyData?.createdDate ? (
                                        <Grid item xs={12} md={6}>
                                            <Typography variant="h6">Created on</Typography>
                                            <Typography>{date(storyData?.createdDate)}</Typography>
                                        </Grid>
                                    ) : null}
                                    {storyData?.ReviewUser?.firstName ? (
                                        <Grid item xs={12} md={6}>
                                            <Typography variant="h6">Reviewed By</Typography>
                                            <Typography>{`${storyData?.ReviewUser?.firstName} ${
                                                storyData?.ReviewUser?.lastName ?? ""
                                            } ${
                                                storyData?.ReviewUser?.email ? `/ ${storyData.ReviewUser.email}` : ""
                                            }`}</Typography>
                                        </Grid>
                                    ) : null}
                                    {storyData?.reviewedDate ? (
                                        <Grid item xs={12} md={6}>
                                            <Typography variant="h6">Reviewed on</Typography>
                                            <Typography>{date(storyData?.reviewedDate)}</Typography>
                                        </Grid>
                                    ) : null}
                                </Grid>
                            </TabPanel>
                        </Container>
                    </Box>
                </>
            ) : (
                <Spinner />
            )}
        </>
    );
};
// Display name for debugging (following design guide)
BranchedStoryDetailsScreen.displayName = "BranchedStoryDetailsScreen";
